import React, { useState, useMemo, useRef } from "react";
import { printReports } from "../../utils";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";

function IconButton({ children, title, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-slate-300 tw-bg-white tw-text-xs tw-text-slate-700 hover:tw-bg-slate-50"
    >
      {children}
    </button>
  );
}

export default function ProductionReport({ filters, range, deviceData, onFavoriteClick, onDownloadCSV, hideControls = false }) {
  const [loading, setLoading] = useState(false);
  const printRef = useRef(null);

  const handleDownloadCSV = () => {
    if (onDownloadCSV) onDownloadCSV();
  };

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
        setLoading(false);
    }, 500);
  };

  const devices = useMemo(() => {
    if (!deviceData || !deviceData.shadowDevices) return [];

    return deviceData.shadowDevices.map(dev => {
      const schedules = (dev.jobSchedules || [])
      .filter(sch => {
           if (!filters || !filters.durationStart || !filters.durationEnd) return true;
           
           try {
               let baseDateStr = filters.shiftDate;
               if (!baseDateStr && filters.durationStart) {
                   baseDateStr = filters.durationStart.substring(0, 10);
               }
               if (!baseDateStr) return true;

               const filterStart = new Date(filters.durationStart).getTime();
               const filterEnd = new Date(filters.durationEnd).getTime();

               const schStart = typeof sch.start_time === 'number' ? sch.start_time : 0;
               const schEnd = typeof sch.end_time === 'number' ? sch.end_time : 86400;

               let startD = new Date(baseDateStr);
               startD.setHours(0,0,0,0);
               let endD = new Date(startD);

               startD.setSeconds(schStart);
               endD.setSeconds(schEnd);
               
               if (schEnd < schStart) {
                   endD.setDate(endD.getDate() + 1);
               }
               
               const s = startD.getTime();
               const e = endD.getTime();
               
               return s < filterEnd && e > filterStart;
           } catch (err) {
               console.warn("Error filtering job", err);
               return true;
           }
      })
      .map(sch => {
        let timeLabel = "";
        try {
             const h = Math.floor(sch.start_time / 3600);
             const m = Math.floor((sch.start_time % 3600) / 60);
             timeLabel = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        } catch(e) {}

        return {
          id: sch.id,
          label: sch.name,
          value: timeLabel,
          type: 'success' 
        };
      });

      if (schedules.length === 0) {
          schedules.push({
              id: 'unassigned',
              label: 'Unassigned jobs',
              type: 'error'
          });
      }

      return {
        id: dev.id,
        name: dev.name,
        schedules: schedules
      };
    });
  }, [deviceData, filters]);


  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-gap-4" ref={printRef}>
      {!hideControls && (
      <div className="tw-flex tw-items-center tw-justify-end tw-relative">
        <div className="tw-flex tw-items-center tw-gap-2">
            <IconButton title="Refresh Data" onClick={handleRefresh}>
                 <RefreshIcon fontSize="small" className={loading ? "tw-animate-spin" : ""} />
            </IconButton>

          <IconButton title="Favourite" onClick={() => {
            if (onFavoriteClick) onFavoriteClick();
          }}>
            <FavoriteBorderIcon fontSize="small" />
          </IconButton>

          <IconButton title="Print report" onClick={() => printReports(printRef.current)}>
            <PrintIcon fontSize="small" />
          </IconButton>

          <IconButton title="Download CSV" onClick={handleDownloadCSV}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
      )}

      {/* Grid Content */}
      <div className="tw-flex-1 tw-overflow-y-auto tw-p-2">
         {devices.length > 0 ? (
             <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 xl:tw-grid-cols-5 tw-gap-4">
                {devices.map((device) => (
                    <div 
                        key={device.id} 
                        className="tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-md tw-flex tw-flex-col tw-h-40"
                        style={{minHeight: '160px'}}
                    >
                        <div className="tw-px-3 tw-py-2 tw-border-b tw-border-slate-200">
                            <span className="tw-font-bold tw-text-slate-700 tw-text-sm">
                                {device.id}
                            </span>
                        </div>
                        <div className="tw-p-2 tw-flex-1 tw-overflow-y-auto tw-flex tw-flex-col tw-gap-2 bg-white">
                            {device.schedules.map((sch) => (
                                <div 
                                    key={sch.id} 
                                    className={`tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-semibold tw-border ${
                                        sch.type === 'error' 
                                            ? 'tw-bg-red-100 tw-text-red-700 tw-border-red-200' 
                                            : 'tw-bg-green-100 tw-text-green-700 tw-border-green-200'
                                    }`}
                                >
                                    <span>{sch.label} {sch.value ? `- ${sch.value}` : ''}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
             </div>
         ) : (
             <div className="tw-flex tw-items-center tw-justify-center tw-h-full">
                 <span className="tw-text-slate-400">No devices found.</span>
             </div>
         )}
      </div>
    </div>
  );
}
